import { fieldStateCss } from '@src/components/common/fieldStateStyles';
import SplitButton from '@src/components/common/SplitButton';
import Switch from '@src/components/common/Switch';
import { MODAL_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { ABOVE_ANCHORED_POPOVER_Z } from '@src/components/common/topLayerZ';
import { MAX_MARKED_LINES } from '@src/services/dubbing/directionPrefill';
import {
  knobCaption,
  knobPresentation,
  unknownMarkers,
} from '@src/services/dubbing/directionSupport';
import { parseLineMarkers } from '@src/services/dubbing/lineMarkers';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import { InfoIconSmall } from '@src/svgs/InfoIconSmall';
import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import AudioTagPicker, { AudioTagPanel, PICKER_WIDTH } from './AudioTagPicker';
import { DUB_DIRECTION_PORTAL_ATTR } from './dubDirectionPortal';
import TaggedLineEditor, { insertLineTag } from './TaggedLineEditor';

import type { DubDirectionCapabilityRead } from './useDubDirectionCapability';
import type {
  DirectionKnob,
  DirectionVerdict,
  DirectionVoiceSummary,
  MarkerDeclaration,
} from '@src/services/dubbing/directionSupport';

/**
 * PLAT-11385 / PLAT-11384 — the direction block of the dub popover. A DIRECTION
 * is a statement of how the lines should be delivered ("slower and warmer");
 * it says nothing about the words or about which voice reads them.
 *
 * Five things live here, in this order:
 *
 * 1. the hint — how each voice in this run can be directed, said before
 *    anything is typed, or said plainly that it cannot be;
 * 2. the note field — shown ONLY when every voice in the run takes a written
 *    direction;
 * 3. the delivery-markers editor (PLAT-11798) — the line's own words with
 *    markers written into them, for the one model that reads them. Shown only
 *    for a run covering exactly one line, because that is what a marked-up line
 *    is. NOT behind a switch: markers are the only way that model can be
 *    directed at all, so gating them would leave it undirectable;
 * 4. the voice-settings panel (PLAT-11797) — the model's own numeric and on/off
 *    controls, drawn from the lowest and highest values the models themselves
 *    declare. Behind a switch that is off by default;
 * 5. the repeat warning (PLAT-11383) — said when the direction about to be sent
 *    is the very same one the last run was given.
 *
 * What is offered follows the voices' models and nothing else. A voice whose
 * model takes nothing shows no control at all: not a field that is greyed out,
 * and not a field that reports afterwards that the note was dropped. When one
 * voice in the run blocks the note, the run is not directed at all and the
 * speaker whose voice blocked it is named (the all-or-nothing rule settled in
 * issue 09 for a section spoken by more than one voice).
 *
 * PLAT-12023 — nothing here is a bare number or a bare bracket. Every control
 * reads with its label, its two ends and one sentence on what moving it does
 * (the words come from `directionSupport.ts`, beside the ranges, never from a
 * lookup in this file); the markers editor offers the markers the voices
 * document, from a list, and points at one it does not know; and a "Best
 * Practices" link beside the hint opens the provider's own guidance.
 */

export { DUB_DIRECTION_PORTAL_ATTR } from './dubDirectionPortal';

export interface DubDirectionFieldProps {
  /** The note as typed. */
  value: string;
  onChange: (value: string) => void;
  /** True while a dub run is in flight — the popover's other controls lock too. */
  disabled?: boolean;
  /** What the run's voices declare they can be asked for. */
  read: DubDirectionCapabilityRead;

  // ── PLAT-11797, the numeric and on/off controls panel ──────────────
  /**
   * Whether the controls panel may be shown at all. A LaunchDarkly switch,
   * default off. When it is off the panel is simply absent — there is no
   * greyed-out version of it and no mention that it exists.
   */
  settingsPanelEnabled?: boolean;
  /** The current position of every control, by control name. */
  knobValues?: Readonly<Record<string, number | boolean>>;
  onKnobChange?: (name: string, value: number | boolean) => void;
  /** Controls whose current value is outside what the models accept, by name. */
  knobsOutOfRange?: readonly string[];

  // ── PLAT-11798, the delivery-markers editor ────────────────────────
  /**
   * True when this run covers a span delivery markers can be written for:
   * one line, or up to `MAX_MARKED_LINES` of them (PLAT-12198).
   */
  markersCoverSpan?: boolean;
  /**
   * Each line of the span, marked up as it now stands, in spoken order; or
   * `null` when the run has no lines to offer (nothing recorded for one, or
   * the read has not landed).
   */
  markersLines?: readonly string[] | null;
  onMarkersChange?: (lines: string[]) => void;
  /**
   * True when a previous run's marked-up text existed but was not put back,
   * because the words underneath it are no longer that line's words. Said out
   * loud rather than left for a person to notice their markers have gone.
   */
  markersStaleDropped?: boolean;
  /**
   * PLAT-12023 — write the markers into the lines for the person (the
   * backend's job, PLAT-12029). Resolves to every line marked up. When absent
   * there is no Auto-tag button at all, not a button that does nothing.
   */
  onAutoTag?: () => Promise<AutoTagResult>;

  // ── PLAT-11383, the repeat warning ─────────────────────────────────
  /**
   * True when the direction about to be sent is the very same one the last run
   * was given.
   */
  repeatsPrevious?: boolean;
  /**
   * PLAT-12042 — who the direction covers, said in the card's title beside
   * it. One direction covers every speaker in a run, so the whole-dub modal
   * says "all speakers" (the default); the Redub Selection popover names
   * the one speaker it is for, or says "these speakers". Delivery markers
   * read "this line" or "these lines": they are written into the lines'
   * own words.
   */
  scopeLabel?: string;
  /**
   * PLAT-12198 — start the card open. The Redub Selection popover passes it:
   * directing the passage is the main reason to re-dub one, so the controls
   * are shown rather than folded behind the title. The whole-dub modal keeps
   * the card closed until something is in effect.
   */
  defaultOpen?: boolean;
  /**
   * PLAT-12198 — drop the card's own top margin, for a host whose layout
   * already spaces its children (the popover's column gap).
   */
  flush?: boolean;
}

const DIRECTION_TOOLTIP_ID = 'dub-direction-tooltip';
const MARKERS_TAKE_ONLY =
  'Audio tags are used for this take only and never saved into the translation.';

// "Alice", "Alice and Bob", "Alice, Bob and Carol".
const listNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

// One sentence per verdict, in both counts, so a line about two speakers reads
// as English rather than as a template.
const VERDICT_PHRASE: Record<DirectionVerdict, { one: string; many: string }> =
  {
    note: {
      one: 'takes a written direction',
      many: 'take a written direction',
    },
    settings: {
      one: 'takes numeric voice settings, not a written direction',
      many: 'take numeric voice settings, not a written direction',
    },
    tags: {
      one: 'takes audio tags written inside the line, not a written direction',
      many: 'take audio tags written inside the line, not a written direction',
    },
    none: { one: 'can’t be directed', many: 'can’t be directed' },
    // The catalog has no entry for the model (or the voice names none): not a
    // refusal by the model, a gap in what the product knows.
    unknown: {
      one: 'is on a model this product doesn’t know how to direct yet',
      many: 'are on a model this product doesn’t know how to direct yet',
    },
  };

interface HintGroup {
  key: string;
  verdict: DirectionVerdict;
  modelLabel: string | null;
  speakerNames: string[];
}

/**
 * Speakers whose voices give the same answer share one line, so a story with
 * six speakers on one model says one thing rather than six.
 */
function groupHints(voices: readonly DirectionVoiceSummary[]): HintGroup[] {
  const groups: HintGroup[] = [];
  const byKey = new Map<string, HintGroup>();
  for (const voice of voices) {
    const key = `${voice.verdict}\u0000${voice.modelLabel ?? ''}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.speakerNames.push(voice.speakerName);
      continue;
    }
    const group: HintGroup = {
      key,
      verdict: voice.verdict,
      modelLabel: voice.modelLabel,
      speakerNames: [voice.speakerName],
    };
    byKey.set(key, group);
    groups.push(group);
  }
  return groups;
}

function hintLine(group: HintGroup): string {
  const many = group.speakerNames.length > 1;
  const phrase = VERDICT_PHRASE[group.verdict][many ? 'many' : 'one'];
  const model = group.modelLabel ? ` (${group.modelLabel})` : '';
  return `${listNames(group.speakerNames)}${
    many ? '’s voices' : '’s voice'
  } ${phrase}${model}.`;
}

/**
 * The step a number control moves in.
 *
 * Derived from the declared range rather than fixed: a control that runs 0 to 1
 * needs hundredths to be usable, while one that runs 0 to 100 does not. Nothing
 * here narrows what may be SENT — the range is what the models declared, and a
 * value outside it is refused rather than rounded.
 */
function knobStep(knob: Extract<DirectionKnob, { type: 'number' }>): number {
  return knob.max - knob.min <= 2 ? 0.01 : 1;
}

type NumberKnob = Extract<DirectionKnob, { type: 'number' }>;

/** How many decimals the control's step needs: 0.01 → 2, 1 → 0. */
const knobDecimals = (knob: NumberKnob): number =>
  Math.max(0, -Math.floor(Math.log10(knobStep(knob))));

/**
 * A value as the field shows it: at most the step's decimals, trailing zeros
 * dropped, always with a point ("0.5", "0.75", "1"). Formatted here rather
 * than by a `type="number"` input, whose rendering follows the browser's
 * locale and showed "0,5" to some people and "0.5" to others.
 */
const formatKnobValue = (knob: NumberKnob, value: number): string =>
  String(Number(value.toFixed(knobDecimals(knob))));

/**
 * What a person typed read as a number: a comma reads as a decimal point.
 * `null` when it is not a number at all. NOT held to the declared range — a
 * value outside it is shown as refused (the field's destructive border and
 * the line under the panel), never quietly rounded into range.
 */
function parseKnobInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

interface NumberKnobRowProps {
  knob: NumberKnob;
  value: number;
  outOfRange: boolean;
  disabled: boolean;
  onChange: (value: number) => void;
}

/**
 * One numeric control, read top to bottom: its label with the value beside it,
 * one sentence on what moving it does, the slider, and the two ends of the
 * slider in words (PLAT-12023, the way ElevenLabs labels its own). The slider
 * is the quick way and cannot leave the range; the value is kept but demoted
 * — a quiet field beside the label for the person who knows the model — and
 * what is typed reaches the run as it is typed, so the slider follows it.
 */
const NumberKnobRow = ({
  knob,
  value,
  outOfRange,
  disabled,
  onChange,
}: NumberKnobRowProps) => {
  const id = `dub-direction-knob-${knob.name}`;
  const step = knobStep(knob);
  // The field shows what is being typed until it is left, so "0." is not
  // snapped back to "0" mid-keystroke; the value underneath still moves with
  // every keystroke that reads as a number.
  const [draft, setDraft] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setDraft(null);
  }, [value, editing]);

  const typed = (raw: string) => {
    setDraft(raw);
    const next = parseKnobInput(raw);
    if (next !== null && next !== value) onChange(next);
  };
  // Leaving the field settles it on the control's step: 0.123 becomes 0.12.
  const leave = () => {
    setEditing(false);
    setDraft(null);
    const next = draft === null ? null : parseKnobInput(draft);
    if (next === null) return;
    const settled = Number(next.toFixed(knobDecimals(knob)));
    if (settled !== value) onChange(settled);
  };
  const fill = Math.min(
    100,
    Math.max(0, ((value - knob.min) / (knob.max - knob.min)) * 100),
  );
  const words = knobPresentation(knob);

  return (
    <KnobRow data-testid={`dub-direction-knob-${knob.name}`}>
      <KnobHead>
        <KnobName>
          <KnobLabel htmlFor={id}>{words.label}</KnobLabel>
          {/* PLAT-12048 — what moving it does, behind the info glyph: the
              name, the value, the slider and its two ends say enough on
              their own. */}
          <KnobInfo
            tabIndex={0}
            aria-label={`About ${words.label}`}
            data-testid={`dub-direction-knob-effect-${knob.name}`}
            data-tooltip-id={DIRECTION_TOOLTIP_ID}
            data-tooltip-content={words.effect}
          >
            <InfoIconSmall />
          </KnobInfo>
        </KnobName>
        <KnobValue
          id={id}
          type="text"
          inputMode="decimal"
          disabled={disabled}
          aria-invalid={outOfRange || undefined}
          $invalid={outOfRange}
          value={draft ?? formatKnobValue(knob, value)}
          onFocus={e => {
            setEditing(true);
            e.target.select();
          }}
          onChange={e => typed(e.target.value)}
          onBlur={leave}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
          }}
        />
      </KnobHead>
      <KnobSlider
        type="range"
        aria-label={`${words.label} slider`}
        aria-valuetext={formatKnobValue(knob, value)}
        min={knob.min}
        max={knob.max}
        step={step}
        disabled={disabled}
        value={Math.min(knob.max, Math.max(knob.min, value))}
        $fill={fill}
        onChange={e => onChange(Number(e.target.value))}
      />
      {words.ends && (
        <KnobEnds aria-hidden="true">
          <span>{words.ends.low}</span>
          <span>{words.ends.high}</span>
        </KnobEnds>
      )}
    </KnobRow>
  );
};

/**
 * PLAT-12075 — one plain sentence for an Auto-tag that could not run. A
 * route the backend does not offer yet (404) is the common case on a
 * deployment that is ahead of its backend; anything else is said as the
 * error says it, trimmed of the repository's "X failed: " prefix.
 */
/**
 * PLAT-12198 — Auto-tag over a passage is one call per line, and one line's
 * failure keeps the rest: a failed line comes back as it was sent.
 */
export interface AutoTagResult {
  lines: string[];
  /** The lines whose call failed, 0-based, with why. */
  failed: { line: number; error: unknown }[];
}

/**
 * The note under the box after an Auto-tag with failures. Names the lines
 * when only some failed; the reason is the first failure's.
 */
export function describeAutoTagResult(result: AutoTagResult): string | null {
  const { lines, failed } = result;
  if (failed.length === 0) return null;
  const reason = describeAutoTagFailure(failed[0].error);
  if (failed.length === lines.length) return `Auto-tag couldn’t run: ${reason}`;
  const numbers = failed.map(f => String(f.line + 1));
  const which =
    numbers.length === 1
      ? `line ${numbers[0]}`
      : `lines ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`;
  return `Auto-tag couldn’t tag ${which}: ${reason}`;
}

export function describeAutoTagFailure(error: unknown): string {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 404) {
    return 'the backend does not offer it yet.';
  }
  const message =
    error instanceof Error && error.message.trim()
      ? error.message.trim()
      : 'something went wrong.';
  const detail = message.replace(/^[\w.]+ failed: /, '');
  return /[.!?]$/.test(detail) ? detail : `${detail}.`;
}

/**
 * PLAT-12079 — the way into the picker when no host can Auto-tag (so there
 * is no split button): a quiet "Add Tag" trigger with the gold caret. The
 * slots between the words are the other way in.
 */
const AddTagButton = ({
  markers,
  disabled,
  ariaLabel,
  onPick,
}: {
  markers: readonly MarkerDeclaration[];
  disabled: boolean;
  ariaLabel: string;
  onPick: (tag: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <MarkerTrigger
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen(o => !o)}
        data-testid="dub-direction-add-tag"
      >
        Add Tag
        <MarkerCaret $open={open}>
          <ChevronDownIcon />
        </MarkerCaret>
      </MarkerTrigger>
      <AudioTagPicker
        open={open}
        anchorRef={triggerRef}
        markers={markers}
        onClose={() => setOpen(false)}
        onPick={tag => {
          setOpen(false);
          onPick(tag);
        }}
      />
    </>
  );
};

const DubDirectionField = ({
  value,
  onChange,
  disabled = false,
  read,
  settingsPanelEnabled = false,
  knobValues = {},
  onKnobChange,
  knobsOutOfRange = [],
  markersCoverSpan = false,
  markersLines = null,
  onMarkersChange,
  markersStaleDropped = false,
  onAutoTag,
  // PLAT-12078 — `repeatsPrevious` is still handed in by the hosts but
  // nothing renders for it: a run of the same direction is another take.
  scopeLabel = 'all speakers',
  defaultOpen = false,
  flush = false,
}: DubDirectionFieldProps) => {
  // The card is closed until a person opens it: directing a dub is an
  // advanced step most runs never take, so the collapsed row says only what
  // kind of direction is on offer and whether one is in effect. It opens on
  // its own when something IS in effect (a previous run's note or values, a
  // value outside the range, tags in the line), because a direction in effect
  // must be seen. `null` = not yet touched by the person.
  const [openChoice, setOpenChoice] = useState<boolean | null>(null);
  // Once shown open, the card stays open until the person closes it: clearing
  // the restored note must not fold it shut under them.
  const shownOpenRef = useRef(false);

  // PLAT-12079 — a tag picked from the split button's chevron lands at the
  // end of the line; a slot between two words lands it there (the editor).
  // PLAT-12198 — over several lines the chevron speaks for the whole passage:
  // the tag opens every line, since each line is voiced on its own and a tag
  // on the first would not carry into the rest. A line already opening with
  // that tag is left alone, so a second pick never doubles it.
  const passageWide = (markersLines?.length ?? 0) > 1;
  const appendTag = (tag: string) => {
    if (!markersLines || markersLines.length === 0) return;
    setAutoTagError(null);
    if (!passageWide) {
      onMarkersChange?.([insertLineTag(markersLines[0], tag, Infinity)]);
      return;
    }
    onMarkersChange?.(
      markersLines.map(line =>
        parseLineMarkers(line).markers.some(
          m => m.beforeWord === 0 && m.tag === tag,
        )
          ? line
          : insertLineTag(line, tag, 0),
      ),
    );
  };
  const addTagLabel = passageWide ? 'Add a tag to every line' : 'Add a tag';
  const [autoTagging, setAutoTagging] = useState(false);
  // PLAT-12075 — why the last Auto-tag could not run, said under the box;
  // cleared by the next successful tag or by editing the text. Before, a
  // refused request looked like a button that does nothing.
  const [autoTagError, setAutoTagError] = useState<string | null>(null);
  const autoTag = async () => {
    if (!onAutoTag || autoTagging) return;
    setAutoTagging(true);
    try {
      const result = await onAutoTag();
      setAutoTagError(describeAutoTagResult(result));
      if (result.failed.length < result.lines.length) {
        onMarkersChange?.(result.lines);
      }
    } catch (error) {
      setAutoTagError(
        `Auto-tag couldn’t run: ${describeAutoTagFailure(error)}`,
      );
    } finally {
      setAutoTagging(false);
    }
  };

  // Nothing is offered and nothing is claimed until the models have answered.
  if (!read.settled) {
    return (
      <DirectionBlock $flush={flush}>
        <HintLine role="status" data-testid="dub-direction-status">
          Checking how these voices can be directed…
        </HintLine>
      </DirectionBlock>
    );
  }

  // An unanswered question is not permission: the field stays away rather than
  // inviting a note the voice provider would refuse. Said as a state with a
  // way out, not as a fault ("Couldn't check…" left a person asking what it
  // meant, PLAT-12023): what happens to the run, and what to do.
  if (read.failed) {
    return (
      <DirectionBlock $flush={flush}>
        <HintLine role="status" data-testid="dub-direction-status">
          Direction controls aren’t available right now, so this run keeps the
          voices’ usual delivery. Try again in a moment.
        </HintLine>
      </DirectionBlock>
    );
  }

  const support = read.support;
  // No voice resolved yet (the mapper is still reporting), so there is nothing
  // to say about voices at all.
  if (!support || support.voices.length === 0) return null;

  // PLAT-11798 / PLAT-12198 — delivery markers are the lines' own words marked
  // up, so they are offered only when this run covers a span they can be
  // written for (up to `MAX_MARKED_LINES` lines) AND those lines' words are to
  // hand. The backend refuses a wider span before any audio is made, so
  // offering the editor there would invite work that is thrown away.
  const markersOffered =
    support.tagsAccepted && markersCoverSpan && markersLines !== null;
  const markersRefusedForSpan = support.tagsAccepted && !markersCoverSpan;
  const markersPassage = markersLines?.join(' ') ?? '';

  // PLAT-11797 — the panel appears only when the switch is on, every voice in
  // the run takes these controls, and there is at least one control that can
  // honestly be drawn.
  const panelOffered =
    settingsPanelEnabled &&
    support.settingsAccepted &&
    support.knobs.length > 0;
  const settingsAdjusted = support.knobs.some(
    knob => (knobValues[knob.name] ?? knob.default) !== knob.default,
  );

  // Nothing at all can be asked of this run, and the span line is not already
  // saying why — a run whose only route is markers over a wider selection has
  // its reason stated there, and saying it twice reads as two problems.
  const nothingOffered =
    !support.noteAccepted &&
    !markersOffered &&
    !panelOffered &&
    !markersRefusedForSpan;

  // What the card offers, said in its title: the settings, the markers, or a
  // written direction (also the title when nothing can be offered, since a
  // direction is what the person came looking for). One direction covers every
  // speaker in a run (§4.8 of the epic's plan), and the title says so, so
  // nobody looks for a per-speaker control.
  const title =
    panelOffered && !support.noteAccepted && !markersOffered
      ? 'Voice Settings'
      : markersOffered && !support.noteAccepted && !panelOffered
        ? 'Audio Tags'
        : 'Voice Direction';
  const scope =
    title === 'Audio Tags'
      ? (markersLines?.length ?? 0) > 1
        ? 'these lines'
        : 'this line'
      : scopeLabel;
  const noteAdded = support.noteAccepted && value.trim() !== '';
  // Tags written into the line are sent with the run, including tags handed
  // back from the last run: the card counts them, so it never says "None"
  // over a line that will be re-dubbed with tags (PLAT-12078).
  const tagsInLine =
    markersOffered && parseLineMarkers(markersPassage).markers.length > 0;
  const inEffect =
    noteAdded ||
    settingsAdjusted ||
    knobsOutOfRange.length > 0 ||
    tagsInLine ||
    markersStaleDropped;
  const open = openChoice ?? (defaultOpen || inEffect || shownOpenRef.current);
  shownOpenRef.current = open;
  const hintGroups = groupHints(support.voices);
  // PLAT-12078 — the one fact the markers editor used to spell out under the
  // box lives here now, with the rest of what the glyph says.
  const hintSentences = [
    ...hintGroups.map(hintLine),
    ...(markersOffered ? [MARKERS_TAKE_ONLY] : []),
  ];
  // Closed, one word says where things stand.
  const summary = nothingOffered
    ? 'Not available'
    : title === 'Voice Settings'
      ? settingsAdjusted
        ? 'Adjusted'
        : 'Default'
      : inEffect
        ? 'Added'
        : 'None';

  return (
    <DirectionBlock
      $flush={flush}
      data-testid={panelOffered ? 'dub-direction-settings' : undefined}
    >
      <CardHeader>
        <HeaderLead>
          <SettingsToggle
            type="button"
            aria-expanded={open}
            aria-controls="dub-direction-body"
            onClick={() => setOpenChoice(!open)}
          >
            <Chevron $open={open}>
              <ChevronDownIcon />
            </Chevron>
            {title}
            <SettingsScope>· {scope}</SettingsScope>
          </SettingsToggle>
          {/* How each voice in this run can be directed: a hover hint on the
              info glyph rather than a line of its own, so the card stays one
              row when closed. The same lines sit in the header for assistive
              tech, visually hidden. */}
          <InfoHint
            tabIndex={0}
            aria-describedby="dub-direction-hint"
            data-tooltip-id={DIRECTION_TOOLTIP_ID}
            data-tooltip-content={hintSentences.join(' ')}
          >
            <InfoIconSmall />
          </InfoHint>
          <HiddenHints id="dub-direction-hint" data-testid="dub-direction-hint">
            {hintSentences.map(sentence => (
              <p key={sentence}>{sentence}</p>
            ))}
          </HiddenHints>
        </HeaderLead>
        {!open && <SettingsSummary>{summary}</SettingsSummary>}
        {/* Open, Reset is offered only once a control has been moved, so an
            untouched panel carries no control that does nothing. */}
        {open && panelOffered && settingsAdjusted && (
          <ResetButton
            type="button"
            disabled={disabled}
            onClick={() =>
              support.knobs.forEach(knob =>
                onKnobChange?.(knob.name, knob.default),
              )
            }
          >
            Reset
          </ResetButton>
        )}
      </CardHeader>

      {open && (
        <CardBody id="dub-direction-body">
          {support.noteAccepted && (
            <>
              <DirectionLabel htmlFor="dub-direction-note">
                Direction (optional)
              </DirectionLabel>
              <DirectionTextarea
                id="dub-direction-note"
                placeholder="How the lines should be delivered, e.g. slower and warmer."
                value={value}
                disabled={disabled}
                onChange={e => onChange(e.target.value)}
              />
            </>
          )}

          {/* PLAT-11798 — the line's own words, with delivery markers written
              into them. No list of valid markers is held here on purpose: the
              voice provider changes which markers it understands without
              saying so, and a list kept in this code would go stale and start
              rejecting real ones. Anything typed is sent, and the provider's
              own answer is what comes back (settled 2026-09-20). */}
          {markersOffered && (
            <MarkersBlock data-testid="dub-direction-markers">
              <MarkersHead>
                <MarkersLabel id="dub-direction-markers-label">
                  Audio Tags
                </MarkersLabel>
                {/* PLAT-12079 — one split button: Auto-tag writes the tags in
                    through the backend (PLAT-12029); its chevron opens the
                    picker to add one at the end. Without a host that can
                    Auto-tag, a plain Add Tag trigger opens the same picker. */}
                {onAutoTag ? (
                  <SplitButton
                    label="Auto-tag"
                    onClick={() => void autoTag()}
                    items={[]}
                    disabled={disabled}
                    busy={autoTagging}
                    busyLabel="Tagging…"
                    variant="secondary"
                    menuAriaLabel={addTagLabel}
                    menuWidth={PICKER_WIDTH}
                    menuZIndex={ABOVE_ANCHORED_POPOVER_Z}
                    menuAttrs={{
                      [DUB_DIRECTION_PORTAL_ATTR]: '',
                      'data-testid': 'dub-direction-marker-menu',
                      role: 'listbox',
                      'aria-label': 'Audio tags',
                    }}
                    menu={
                      support.markers.length > 0
                        ? close => (
                            <AudioTagPanel
                              markers={support.markers}
                              onPick={tag => {
                                close();
                                appendTag(tag);
                              }}
                            />
                          )
                        : () => (
                            <HintLine>
                              No list of tags is known for these voices.
                            </HintLine>
                          )
                    }
                  />
                ) : (
                  support.markers.length > 0 && (
                    <AddTagButton
                      markers={support.markers}
                      disabled={disabled}
                      ariaLabel={addTagLabel}
                      onPick={appendTag}
                    />
                  )
                )}
              </MarkersHead>
              <TaggedLineEditor
                taggedLines={markersLines ?? []}
                markers={support.markers}
                // Auto-tag's result replaces every line, so an edit made
                // while it runs would be overwritten.
                disabled={disabled || autoTagging}
                onChange={next => {
                  setAutoTagError(null);
                  onMarkersChange?.(next);
                }}
              />
              {autoTagError && (
                <UnknownMarkerLine
                  role="alert"
                  data-testid="dub-direction-auto-tag-error"
                >
                  {autoTagError}
                </UnknownMarkerLine>
              )}
              {support.markers.length === 0 && !onAutoTag && (
                <HintLine>
                  No list of tags is known for these voices, so none can be
                  added here.
                </HintLine>
              )}
              {unknownMarkers(markersPassage, support.markers).map(tag => (
                <UnknownMarkerLine
                  key={tag}
                  role="status"
                  data-testid="dub-direction-marker-unknown"
                >
                  [{tag}] isn’t a tag these voices document. It will be sent as
                  written and may be read aloud or ignored.
                </UnknownMarkerLine>
              ))}
              {markersStaleDropped && (
                <HintLine data-testid="dub-direction-markers-stale">
                  {(markersLines?.length ?? 0) > 1
                    ? 'Some tags from the last run were written over different words, so they have not been brought back. Those lines’ own words are shown instead.'
                    : 'The tags from the last run were written over different words, so they have not been brought back. This line’s own words are shown instead.'}
                </HintLine>
              )}
            </MarkersBlock>
          )}
          {markersRefusedForSpan && (
            <HintLine data-testid="dub-direction-markers-span">
              Audio tags can be written into up to {MAX_MARKED_LINES} lines from
              one speaker at a time, so they are offered only when you select
              that many lines or fewer, all said by one speaker.
            </HintLine>
          )}

          {/* PLAT-11797 — the model's own numeric and on/off controls, behind
              a switch that is off by default. The lowest and highest value of
              each control comes from what the models declare, read back from
              the same route the engine validates against, so the panel can
              never offer a number the run would be refused for. */}
          {panelOffered && (
            <>
              <SettingsStack data-testid="dub-direction-settings-stack">
                {support.knobs.map(knob =>
                  knob.type === 'number' ? (
                    <NumberKnobRow
                      key={knob.name}
                      knob={knob}
                      // Falls back to the control's own declared value for the
                      // render before the panel is seeded.
                      value={Number(knobValues[knob.name] ?? knob.default)}
                      outOfRange={knobsOutOfRange.includes(knob.name)}
                      disabled={disabled}
                      onChange={next => onKnobChange?.(knob.name, next)}
                    />
                  ) : (
                    <KnobRow
                      key={knob.name}
                      data-testid={`dub-direction-knob-${knob.name}`}
                    >
                      <KnobHead>
                        <KnobName>
                          <KnobLabel as="span">
                            {knobPresentation(knob).label}
                          </KnobLabel>
                          <KnobInfo
                            tabIndex={0}
                            aria-label={`About ${knobPresentation(knob).label}`}
                            data-testid={`dub-direction-knob-effect-${knob.name}`}
                            data-tooltip-id={DIRECTION_TOOLTIP_ID}
                            data-tooltip-content={knobPresentation(knob).effect}
                          >
                            <InfoIconSmall />
                          </KnobInfo>
                        </KnobName>
                        <Switch
                          tiny
                          label=""
                          ariaLabel={knobPresentation(knob).label}
                          disabled={disabled}
                          // Same fallback as the number rows: without it the
                          // switch would show as off before the panel is seeded,
                          // even though the model's own value is on.
                          checked={
                            (knobValues[knob.name] ?? knob.default) === true
                          }
                          onClick={() =>
                            onKnobChange?.(
                              knob.name,
                              (knobValues[knob.name] ?? knob.default) !== true,
                            )
                          }
                        />
                      </KnobHead>
                    </KnobRow>
                  ),
                )}
              </SettingsStack>
              {support.undeclaredKnobs.length > 0 && (
                <HintLine>
                  {listNames(support.undeclaredKnobs.map(knobCaption))}{' '}
                  {support.undeclaredKnobs.length > 1 ? 'are' : 'is'} accepted
                  by these voices, but no range has been declared for{' '}
                  {support.undeclaredKnobs.length > 1 ? 'them' : 'it'}, so{' '}
                  {support.undeclaredKnobs.length > 1 ? 'they are' : 'it is'}{' '}
                  not shown here.
                </HintLine>
              )}
              {knobsOutOfRange.length > 0 && (
                <OutOfRangeLine
                  role="alert"
                  data-testid="dub-direction-out-of-range"
                >
                  {listNames(knobsOutOfRange.map(knobCaption))}{' '}
                  {knobsOutOfRange.length > 1 ? 'are' : 'is'} outside what these
                  voices accept, so the dub can’t be started until{' '}
                  {knobsOutOfRange.length > 1 ? 'they are' : 'it is'} back in
                  range.
                </OutOfRangeLine>
              )}
            </>
          )}

          {/* The voices take different kinds of direction (or none), and a
              run carries one direction for every speaker, so none is offered.
              Said with the way out, not just the wall. */}
          {nothingOffered &&
            (support.voices.every(voice => voice.verdict === 'unknown') ? (
              // Nobody declared these models: the way out is a declaration
              // (or a declared voice), not a different kind of direction.
              <BlockedLine role="status" data-testid="dub-direction-blocked">
                {listNames(support.blockedBy)}
                {support.blockedBy.length > 1
                  ? '’s voices are'
                  : '’s voice is'}{' '}
                on a model this product doesn’t know how to direct yet, so this
                run can’t be directed. Ask for the model to be declared, or
                choose a voice on a model that is.
              </BlockedLine>
            ) : (
              <BlockedLine role="status" data-testid="dub-direction-blocked">
                {listNames(support.blockedBy)}
                {support.blockedBy.length > 1 ? '’s voices' : '’s voice'} can’t
                take a written direction, so this run can’t be directed. One
                direction covers every speaker in a run; to direct it, give
                every speaker a voice that takes the same kind of direction.
              </BlockedLine>
            ))}
        </CardBody>
      )}
      <Tooltip id={DIRECTION_TOOLTIP_ID} style={MODAL_TOOLTIP_STYLE} />
    </DirectionBlock>
  );
};

export default DubDirectionField;

// The direction block of the popover: plain type in the tokens the popover
// already uses and nothing more — the design pass comes later and changes only
// the skin.
// The direction block is its own bordered card under the speaker list, in the
// list's own border and radius, so the two read as two sections rather than
// one run of grey text. Inside it the hint is the card's quiet subtitle
// (field-level hint: one step quieter than the popover's subtitle) and the
// controls sit under a hairline.
const DirectionBlock = styled.div<{ $flush?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: ${p => (p.$flush ? '0' : 'var(--spacing-md)')};
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  flex-shrink: 0;
`;

const HeaderLead = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  min-width: 0;
`;

// The info glyph: a quiet 20px hit area, lit on hover like the other quiet
// controls, with the neutral focus ring for the keyboard.
const InfoHint = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  color: var(--text-muted);
  cursor: default;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

// Visually hidden, read by assistive tech (and the tests) as the glyph's
// description.
const HiddenHints = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;

  p {
    margin: 0;
  }
`;

const HintLine = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--text-muted);
`;

const BlockedLine = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const DirectionLabel = styled.label`
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

// Typed text names its token rather than inheriting. The popover is portaled to
// document.body, which sets no colour of its own, so `color: inherit` on a form
// control resolved to the browser's default black on this dark surface. The
// three steps are the ones the rest of the app already uses: --foreground for a
// real value (the same token the translation popover's own text field uses),
// --text-secondary for a locked field, --text-muted for placeholder text.
const DirectionTextarea = styled.textarea`
  min-height: 64px;
  resize: vertical;
  padding: 8px 10px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  font-family: inherit;
  font-size: var(--text-sm);
  color: var(--foreground);

  &::placeholder {
    color: var(--text-muted);
  }

  &:disabled {
    color: var(--text-secondary);
  }
`;

// The markers editor and the controls panel sit under the note field, in the
// same plain tokens — the design pass comes later and changes only the skin.
const MarkersBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: var(--spacing-sm);
`;

// The label with the Auto-tag action pinned to its right, the way the
// translation editors pin Revert to the row it acts on.
const MarkersHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
`;

const MarkersLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

// The Insert Marker trigger: the setting-row value pill (borderless, gold
// caret), since it opens a menu of values rather than performing an action.
const MarkerTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: -8px;
  padding: 4px 8px;
  background: none;
  border: none;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MarkerCaret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: var(--accent);
  transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  transition: transform var(--transition-fast);

  svg {
    width: 12px;
    height: 12px;
  }
`;

// A marker the list does not know: the warning colour, since the provider may
// still read it — this is a doubt, not a refusal.
const UnknownMarkerLine = styled.p`
  margin: 0;
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  color: var(--accent);
`;

// The controls panel is a stack of setting rows — label left, control right,
// a hairline between rows — in the shape the design system gives setting
// rows (docs/design-system.md, "Setting rows"). Gold is the colour of the
// creative controls here (slider, switch); the typed value is a bordered
// field primitive with the neutral focus ring.
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 28px;
`;

// Everything the card offers, under the header. No divider of its own: the
// setting rows draw their hairlines, and one more above them read as two.
const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: var(--spacing-xs);
`;

// The disclosure: the title is the button, with the chevron the design
// system gives a header that collapses (one glyph both ways, turning).
const SettingsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 0 -4px;
  padding: 2px 6px 2px 4px;
  background: none;
  border: none;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 1px var(--focus-ring);
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  color: var(--text-muted);
  transform: rotate(${p => (p.$open ? '0deg' : '-90deg')});
  transition: transform var(--transition-fast);
`;

const SettingsScope = styled.span`
  font-weight: var(--font-weight-normal);
  color: var(--text-muted);
`;

const SettingsSummary = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
`;

// One column: each control is a block of its own (label, sentence, slider,
// ends), under a hairline. The first row's hairline is the line under
// whatever sits above it — the header, or the markers editor.
// PLAT-12050 — two settings to a row: with the sentence behind the glyph
// (PLAT-12048) a row is a name, a value, a slider and two end words, and
// stacking them spent height the popover and the modal do not have. One
// column again only where two cannot fit.
const SettingsStack = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--spacing-lg);
  margin-top: var(--spacing-xs);

  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

// Tertiary: text only, hover shows the row background, as the other quiet
// actions in the popover do.
const ResetButton = styled.button`
  padding: 2px 8px;
  background: none;
  border: none;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: var(--text-xs);
  color: var(--text-secondary);
  cursor: pointer;
  transition: background var(--transition-fast);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// The slider-with-ends pattern (docs/design-system.md, "Slider With Ends"):
// the number is never the headline. Label (with its info glyph) and the quiet
// value on one line, the gold hairline, and the two end words under its ends;
// the sentence on what moving it does sits behind the glyph (PLAT-12048).
const KnobRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  min-width: 0;
  border-top: 1px solid var(--border-subtle);
`;

const KnobHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  min-height: 24px;
`;

const KnobLabel = styled.label`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  white-space: nowrap;
  overflow: hidden;
`;

// The label and its info glyph, as one unit on the head's left.
const KnobName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  min-width: 0;
`;

// PLAT-12048 — the one sentence on what moving the control does, on hover
// (and on focus, for the keyboard), the way Keep Original Music explains
// itself: `color: var(--text-secondary); cursor: help`.
const KnobInfo = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
  cursor: help;
`;

// The two ends of the slider in words, under its two ends.
const KnobEnds = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  line-height: var(--leading-text-xs);
  color: var(--text-muted);
`;

// A hairline track with the gold fill up to the thumb, as the app's other
// sliders draw it; the keyboard focus ring is the neutral one.
const KnobSlider = styled.input<{ $fill: number }>`
  flex: 1 1 auto;
  min-width: 0;
  height: 2px;
  margin: 0;
  appearance: none;
  -webkit-appearance: none;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    var(--accent) 0%,
    var(--accent) ${p => p.$fill}%,
    var(--border) ${p => p.$fill}%,
    var(--border) 100%
  );
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
    transition: transform var(--transition-fast);
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: none;
    border-radius: 50%;
    background: var(--accent);
    cursor: pointer;
    transition: transform var(--transition-fast);
  }

  &:hover:not(:disabled)::-webkit-slider-thumb,
  &:active:not(:disabled)::-webkit-slider-thumb {
    transform: scale(1.15);
  }

  &:hover:not(:disabled)::-moz-range-thumb,
  &:active:not(:disabled)::-moz-range-thumb {
    transform: scale(1.15);
  }

  &:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }

  &:focus-visible::-moz-range-thumb {
    box-shadow: 0 0 0 2px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// The value, demoted: a quiet number beside the label that is still a field.
// Borderless and muted at rest so it reads as "0.54" rather than as a second
// control; the standard hover → focus escalation once a pointer or the
// keyboard reaches it. A value the models would refuse keeps the destructive
// border at every state instead (PLAT-11797).
const KnobValue = styled.input<{ $invalid: boolean }>`
  flex: 0 0 52px;
  width: 52px;
  height: 24px;
  padding: 0 6px;
  box-sizing: border-box;
  text-align: right;
  background: transparent;
  border-radius: var(--radius);
  font-family: inherit;
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
  color: var(--text-muted);
  ${fieldStateCss}
  border-color: ${p => (p.$invalid ? 'var(--destructive)' : 'transparent')};

  &:hover:not(:disabled) {
    color: var(--foreground);
    border-color: ${p =>
      p.$invalid ? 'var(--destructive)' : 'var(--text-muted)'};
  }

  &:focus {
    color: var(--foreground);
    border-color: ${p =>
      p.$invalid ? 'var(--destructive)' : 'var(--focus-border)'};
    box-shadow: 0 0 0 1px
      ${p => (p.$invalid ? 'var(--destructive)' : 'var(--focus-ring)')};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const OutOfRangeLine = styled.p`
  margin: 0;
  margin-top: var(--spacing-xs);
  font-size: var(--text-xs);
  color: var(--destructive);
`;
